import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../../../../redux/store";
import { Share2 } from "lucide-react";
import { fetchPosts } from "../../../../../../../redux/blogSlice";

const POSTS_PER_PAGE = 8;

const MoreNews: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { posts } = useSelector((state: RootState) => state.blog);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  const page = parseInt(searchParams.get("page") || "1", 10);
  const [filteredPosts, setFilteredPosts] = useState(posts);

  useEffect(() => {
    if (!posts.length) dispatch(fetchPosts());
  }, [dispatch, posts.length]);

  useEffect(() => {
    const q = searchQuery.toLowerCase();
    setFilteredPosts(
      posts.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.description?.toLowerCase().includes(q) ||
          p.category?.toLowerCase().includes(q)
      )
    );
  }, [searchQuery, posts]);

  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);
  const start = (page - 1) * POSTS_PER_PAGE;
  const currentPosts = filteredPosts.slice(start, start + POSTS_PER_PAGE);

  const handlePageClick = (pg: number) => {
    setSearchParams({ page: pg.toString() });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleShare = (post: any) => {
    const url = `${window.location.origin}/${post.slug}`;
    if (navigator.share) {
      navigator.share({ title: post.title, text: post.description, url });
    } else {
      navigator.clipboard.writeText(url);
      alert("Link copied to clipboard");
    }
  };

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <h2 style={styles.logo}>ARELUXE News</h2>
        <input
          type="text"
          placeholder="Search news..."
          style={styles.search}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* News Grid */}
      <div style={styles.grid}>
        {currentPosts.map((post) => (
          <div key={post.id} style={styles.card}>
            <div style={styles.imageWrap}>
              <img src={post.featuredImage} alt={post.title} style={styles.image} />
              <Share2
                size={20}
                onClick={() => handleShare(post)}
                style={styles.shareIcon}
                color="#fff"
              />
            </div>
            <div style={styles.cardContent}>
              <span style={styles.category}>{post.category}</span>
              <h3 style={styles.title}>{post.title}</h3>
              <p style={styles.description}>{post.description}</p>
              <button
                style={styles.readMoreBtn}
                onClick={() => navigate(`/${post.slug}`)}
              >
                Read More →
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div style={styles.pagination}>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => (
          <span
            key={pg}
            onClick={() => handlePageClick(pg)}
            style={{
              ...styles.pageButton,
              backgroundColor: pg === page ? "#c084fc" : "transparent",
              color: pg === page ? "#000" : "#fff",
            }}
          >
            {pg}
          </span>
        ))}
      </div>

      {/* Footer */}
      <div style={styles.footer}>© ARELUXE {new Date().getFullYear()}</div>
    </div>
  );
};

export default MoreNews;

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    background: "#0a0014",
    color: "#fff",
    paddingTop: 70,
    paddingBottom: 60,
    minHeight: "100vh",
  },
  header: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    background: "linear-gradient(160deg, #0a0014, #1e003a, #bf9b30, #ffdc73, #111)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 20px",
    zIndex: 1000,
    boxShadow: "0 1px 5px rgba(0,0,0,0.3)",
  },
  logo: {
    fontSize: 20,
    fontWeight: 700,
    color: "#c084fc",
  },
  search: {
    background: "#1e003a",
    color: "#fff",
    border: "1px solid #ffdc73",
    borderRadius: 8,
    padding: "6px 12px",
    fontSize: 14,
    width: "50%",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 24,
    padding: 20,
  },
  card: {
    background: "linear-gradient(145deg, #1e003a, #ffdc73)",
    borderRadius: 12,
    boxShadow: "0 0 12px rgba(192,132,252,0.2)",
    overflow: "hidden",
    transition: "transform 0.2s ease",
  },
  imageWrap: {
    position: "relative",
  },
  image: {
    width: "100%",
    height: 160,
    objectFit: "cover",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  shareIcon: {
    position: "absolute",
    top: 10,
    right: 10,
    cursor: "pointer",
    background: "#c084fc",
    borderRadius: "50%",
    padding: 4,
  },
  cardContent: {
    padding: 16,
  },
  category: {
    fontSize: 12,
    color: "#f472b6",
    border: "1px solid #f472b6",
    padding: "2px 8px",
    borderRadius: 20,
    display: "inline-block",
    marginBottom: 8,
    textTransform: "uppercase",
  },
  title: {
    fontSize: 17,
    fontWeight: 600,
    marginBottom: 6,
  },
  description: {
    fontSize: 14,
    color: "#ccc",
    marginBottom: 10,
  },
  readMoreBtn: {
    padding: "6px 12px",
    border: "1px solid #f472b6",
    color: "#f472b6",
    background: "transparent",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 13,
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    gap: 12,
    marginTop: 30,
    flexWrap: "wrap",
  },
  pageButton: {
    padding: "6px 12px",
    border: "1px solid #c084fc",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 14,
  },
  footer: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    background: "#0a0014",
    color: "#aaa",
    textAlign: "center",
    padding: "10px 0",
    fontSize: 13,
    borderTop: "1px solid #333",
    zIndex: 1000,
  },
};
